import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ProjectWizardLayout } from '../components/Wizard/ProjectWizardLayout';
import { ReviewCard } from '../components/Wizard/ReviewCard';
import { useWizardStore } from '../store/wizardStore';
import { supabase } from '../lib/supabase';
import wizardSchema from '../data/wizardSchema.json';

export const ProjectWizardReviewPage: React.FC = () => {
  const navigate = useNavigate();
  const {
    currentDraft,
    isLoading,
    error,
    loadDraft,
    submitProject,
    clearDraft
  } = useWizardStore();

  const totalSteps = wizardSchema.steps.length + 2; // +2 for documents and review steps

  // Load draft if not already loaded
  useEffect(() => {
    const initializeDraft = async () => {
      const savedDraftId = localStorage.getItem('currentDraftId');
      if (savedDraftId && !currentDraft) {
        try {
          await loadDraft(savedDraftId);
        } catch (error) {
          console.error('Failed to load draft:', error);
          navigate('/projects/new');
        }
      } else if (!savedDraftId) {
        navigate('/projects/new');
      }
    };

    initializeDraft();
  }, [currentDraft, loadDraft, navigate]);

  const handleEdit = () => {
    // Go back to first step to allow editing
    navigate('/projects/new/step/1');
  };

  const handleSubmit = async () => {
    if (!currentDraft) return;

    try {
      const projectId = await submitProject(currentDraft.id);
      
      // Clear draft data
      localStorage.removeItem('currentDraftId');
      clearDraft();
      
      // Redirect to project page with success message
      navigate(`/projects/${projectId}`, {
        state: { message: 'Votre projet a été soumis avec succès !' }
      });
    } catch (error: any) {
      console.error('Failed to submit project:', error);
    }
  };

  const handlePrevious = () => {
    navigate('/projects/new/documents');
  };

  if (!currentDraft) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const allAnswersComplete = Object.keys(currentDraft.answers).length === wizardSchema.steps.length;
  const allDocumentsUploaded = wizardSchema.requiredDocuments.every(reqDoc =>
    currentDraft.documents.some(doc => doc.document_subtype === reqDoc.type.toUpperCase())
  );
  const canSubmit = allAnswersComplete && allDocumentsUploaded;

  // Helper function to generate meaningful project title
  const generateMeaningfulTitle = (answers: any) => {
    const domaine = answers.domaine || 'ESS';
    const solution = answers.solution;
    const publicConcerne = answers.public_concerne;
    
    if (solution && publicConcerne) {
      return `${solution} pour ${publicConcerne}`;
    } else if (solution) {
      return `${solution} - ${domaine}`;
    } else if (domaine) {
      return `Projet ${domaine}`;
    } else {
      return `Projet ESS ${new Date().toLocaleDateString('fr-FR')}`;
    }
  };

  return (
    <ProjectWizardLayout
      currentStep={totalSteps}
      totalSteps={totalSteps}
      onPrevious={handlePrevious}
      canGoNext={false}
      canGoPrevious={true}
      isLoading={isLoading}
      title="Récapitulatif et soumission"
    >
      <ReviewCard
        answers={currentDraft.answers}
        documents={currentDraft.documents}
        onEdit={handleEdit}
        onSubmit={handleSubmit}
        isSubmitting={isLoading}
      />
      
      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg mx-8 mb-4">
          <p className="text-red-600 text-sm">{error}</p>
        </div>
      )}
    </ProjectWizardLayout>
  );
};